/**
 * AiSettings Component
 *
 * Configuration panel for AI features (API key, server proxy settings)
 */

import { useCallback, useState } from 'react';
import { cn } from '../../lib/utils';
import { useAIStore, selectHasApiKey } from '../aiStore';

interface AiSettingsProps {
  className?: string;
  onClose?: () => void;
}

/**
 * Main AiSettings component
 */
export function AiSettings({ className, onClose }: AiSettingsProps) {
  const config = useAIStore((state) => state.config);
  const setApiKey = useAIStore((state) => state.setApiKey);
  const setUseServerProxy = useAIStore((state) => state.setUseServerProxy);
  const hasApiKey = useAIStore(selectHasApiKey);

  const [apiKeyInput, setApiKeyInput] = useState(config.apiKey || '');
  const [showApiKey, setShowApiKey] = useState(false);
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [testError, setTestError] = useState<string | null>(null);

  const handleSaveApiKey = useCallback(() => {
    const trimmed = apiKeyInput.trim();
    setApiKey(trimmed || null);
  }, [apiKeyInput, setApiKey]);

  const handleClearApiKey = useCallback(() => {
    setApiKeyInput('');
    setApiKey(null);
  }, [setApiKey]);

  const handleTestConnection = useCallback(async () => {
    setTestStatus('testing');
    setTestError(null);

    try {
      // Test server proxy first if enabled
      if (config.useServerProxy) {
        const response = await fetch('/api/ai/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: 'Hello, this is a test.' }),
        });

        if (!response.ok) {
          throw new Error(`Server returned ${response.status}`);
        }

        setTestStatus('success');
        return;
      }

      // Test direct API with user key
      if (!config.apiKey) {
        throw new Error('No API key configured');
      }

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': config.apiKey,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 10,
          messages: [{ role: 'user', content: 'Say "OK"' }],
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error?.message || `API returned ${response.status}`);
      }

      setTestStatus('success');
    } catch (error) {
      setTestStatus('error');
      setTestError(error instanceof Error ? error.message : 'Connection test failed');
    }
  }, [config]);

  return (
    <div className={cn('bg-gray-800 rounded-lg overflow-hidden', className)}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">AI Settings</h3>
          <p className="text-sm text-gray-400 mt-0.5">Configure AI assistance features</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-200 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <div className="p-4 space-y-6">
        {/* Connection Status */}
        <div className="flex items-center gap-3 p-3 bg-gray-900/50 rounded-lg">
          <div
            className={cn(
              'w-3 h-3 rounded-full',
              hasApiKey ? 'bg-green-500' : 'bg-yellow-500'
            )}
          />
          <span className="text-sm text-gray-300">
            {hasApiKey ? 'AI features available' : 'AI features not configured'}
          </span>
        </div>

        {/* Server Proxy Toggle */}
        <div className="space-y-2">
          <label className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-200">Use Server Proxy</span>
              <p className="text-xs text-gray-400 mt-0.5">
                Route API calls through the server (recommended)
              </p>
            </div>
            <button
              onClick={() => setUseServerProxy(!config.useServerProxy)}
              className={cn(
                'relative w-11 h-6 rounded-full transition-colors',
                config.useServerProxy ? 'bg-blue-600' : 'bg-gray-600'
              )}
            >
              <span
                className={cn(
                  'absolute top-1 w-4 h-4 bg-white rounded-full transition-transform',
                  config.useServerProxy ? 'left-6' : 'left-1'
                )}
              />
            </button>
          </label>
        </div>

        {/* API Key Input (shown when server proxy is off) */}
        {!config.useServerProxy && (
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-medium text-gray-200">Anthropic API Key</span>
              <p className="text-xs text-gray-400 mt-0.5">
                Your API key is stored locally and never sent to our servers
              </p>
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder="sk-ant-..."
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                >
                  {showApiKey ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              <button
                onClick={handleSaveApiKey}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
              >
                Save
              </button>
            </div>
            {config.apiKey && (
              <button
                onClick={handleClearApiKey}
                className="text-sm text-red-400 hover:text-red-300"
              >
                Clear saved key
              </button>
            )}
          </div>
        )}

        {/* Test Connection */}
        <div className="space-y-2">
          <button
            onClick={handleTestConnection}
            disabled={testStatus === 'testing' || !hasApiKey}
            className={cn(
              'w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              testStatus === 'testing'
                ? 'bg-gray-600 text-gray-400 cursor-wait'
                : 'bg-gray-700 hover:bg-gray-600 text-gray-200'
            )}
          >
            {testStatus === 'testing' ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Testing connection...
              </span>
            ) : (
              'Test Connection'
            )}
          </button>

          {testStatus === 'success' && (
            <div className="flex items-center gap-2 text-sm text-green-400">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Connection successful!
            </div>
          )}

          {testStatus === 'error' && (
            <div className="flex items-start gap-2 text-sm text-red-400">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span>
                Connection failed: {testError || 'Unknown error'}
              </span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-3 bg-blue-900/20 border border-blue-800/50 rounded-lg">
          <div className="flex gap-2 text-sm text-blue-300">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="font-medium">About AI Features</p>
              <p className="text-blue-400 mt-1">
                AI assistance uses Claude to explain errors, answer questions about your model,
                and provide suggestions. When using the server proxy, API calls are routed
                through our server. When using your own API key, calls go directly to Anthropic.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AiSettings;
